import React, { useState } from "react";
import { Button } from "@chemaxon/design-system/button";
import { useNavigate } from "react-router-dom";

import ViewContainer from "@chemaxon/terminus-client/ui/components/view-container/ViewContainer";
import MemberList from "@chemaxon/terminus-client/ui/components/team-members/MemberList";
import TeamService, {
  useGetTeams,
} from "@chemaxon/terminus-client/ui/services/TeamService";
import ContainerWithLoaderAndError from "@chemaxon/terminus-client/ui/components/container-with-loader-and-error/ContainerWithLoaderAndError";
import Subheader from "@chemaxon/terminus-client/ui/components/subheader/Subheader";
import {
  OnlyForTeamAdmin,
  Restricted,
} from "@chemaxon/terminus-client/ui/utils/Restricted";
import {
  PlatformDomainObjectType,
  PlatformPermission,
} from "@chemaxon/terminus-client/ui/models/Permission";
import {
  useGetTeamMembers,
  useRemoveTeamMember,
} from "@chemaxon/terminus-client/ui/services/TeamMemberService";
import { adaptUseQueryResponseForContainerWithLoader } from "@chemaxon/terminus-client/ui/utils/queryHelpers";
import { AppRoutePath } from "@chemaxon/terminus-client/ui/utils/routes";
import { useGetCurrentTeam } from "@chemaxon/terminus-client/ui/services/UserInfoService";
import { TeamMember } from "@chemaxon/terminus-client/ui/models/TeamMember";
import ConfirmDialog from "@chemaxon/terminus-client/ui/components/confirmation/ConfirmDialog";
import RemoveMemberButton from "@chemaxon/terminus-client/ui/components/removeMemberButton/RemoveMemberButton";
import { usePermissions } from "@chemaxon/terminus-client/ui/utils/usePermissions";
import InformationDialog from "@chemaxon/terminus-client/ui/components/information/InformationDialog";
import { ConditionalMessage } from "@chemaxon/terminus-client/ui/components/alerts/ConditionalMessage";
import { isExternalGroupManagement } from "@chemaxon/terminus-client/ui/models/Team";

const ListTeamMembersView = () => {
  const navigate = useNavigate();
  const getCurrentTeamQueryResult = useGetCurrentTeam();
  const getTeamsQueryResult = useGetTeams();
  const getTeamMembersQueryResult = useGetTeamMembers();
  const mutateRemoveTeamMember = useRemoveTeamMember();
  const { checkPermission } = usePermissions();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [teamMemberToRemove, setTeamMemberToRemove] =
    useState<TeamMember | null>(null);

  const principalCurrenTeam = getCurrentTeamQueryResult.data;
  const teams = getTeamsQueryResult.data;

  if (teams === undefined || principalCurrenTeam === undefined) {
    return null;
  }

  const teamMembers = adaptUseQueryResponseForContainerWithLoader(
    getTeamMembersQueryResult,
  );

  const currentTeam = TeamService.lookupTeamById(teams, principalCurrenTeam.id);

  if (currentTeam === undefined) {
    return null;
  }

  const addTeamMembersButton =
    currentTeam.usedSeatCount <= currentTeam.teamSize ? (
      <Restricted
        to={{
          platformDomainObjectType: PlatformDomainObjectType.USER,
          platformPermission: PlatformPermission.CREATE,
        }}
      >
        <Button
          colorVariant="primary"
          id="addTeamMemberButton"
          onClick={() => {
            navigate(AppRoutePath.TeamMemberAdd);
          }}
        >
          Add team members
        </Button>
      </Restricted>
    ) : (
      ""
    );

  const confirmDialog = (
    <ConfirmDialog
      isShown={teamMemberToRemove !== null}
      title={
        "Are you sure, you want to remove " +
        (teamMemberToRemove ? teamMemberToRemove.userName : null) +
        "?"
      }
      submit={() =>
        teamMemberToRemove &&
        mutateRemoveTeamMember
          .mutateAsync(teamMemberToRemove.id)
          .then(() => hideConfirmDialog())
          .catch(e => {
            setTeamMemberToRemove(null);
            setErrorMessage(e.message || "Unable to remove team member");
          })
      }
      cancel={() => hideConfirmDialog()}
      destructive={true}
    />
  );

  const hideConfirmDialog = () => setTeamMemberToRemove(null);

  const showConfirmDialog = (teamMember: TeamMember) =>
    setTeamMemberToRemove(teamMember);

  const closeErrorDialog = () => setErrorMessage(null);

  const renderRemoveButton = (teamMember: TeamMember) => (
    <RemoveMemberButton
      hasPermission={checkPermission(
        PlatformDomainObjectType.USER,
        PlatformPermission.DELETE,
      )}
      onClick={() => showConfirmDialog(teamMember)}
    />
  );

  return (
    <React.Fragment>
      <OnlyForTeamAdmin>
        <Subheader
          title={`Members of ${currentTeam.teamName} team - ${currentTeam.usedSeatCount}/${currentTeam.teamSize}`}
          button={addTeamMembersButton}
        />
        <ConditionalMessage condition={isExternalGroupManagement(currentTeam.userAndGroupManagementMode)}>
          User add and delete operations are disabled because user and group
          membership management in this team is configured to be done from an
          external IdP.
        </ConditionalMessage>

        <ViewContainer id={"listTeamMembersContainer"} sx={{ mt: 33}}>
          <ContainerWithLoaderAndError
            data={teamMembers}
            content={teamMembers => {
              return (
                <>
                  <MemberList
                    memberList={teamMembers}
                    renderRemoveButton={renderRemoveButton}
                  />
                  {confirmDialog}

                  <InformationDialog
                    isShown={errorMessage !== null}
                    close={() => closeErrorDialog()}
                  >
                    <div>{errorMessage}</div>
                  </InformationDialog>
                </>
              );
            }}
          />
        </ViewContainer>
      </OnlyForTeamAdmin>
    </React.Fragment>
  );
};

export default ListTeamMembersView;
